import type {DashboardData, Event, EventAddress, EventComboBox, EventForm, EventSearch} from "~/types/event";
import type {Ticket} from "~/types/ticket";

export const useEvent = () => {
  const baseUrl = '/events'
  const {fetch} = useAjax()

  const typeOptions = [
    {label: 'Evento Presencial', value: 'IN_PERSON'},
    {label: 'Evento Online', value: 'ONLINE'}
  ]
  const statusOptions = [
    {label: 'Rascunho', value: 'DRAFT'},
    {label: 'Publicado', value: 'PUBLISHED'},
    {label: 'Fechado', value: 'CLOSED'},
    {label: 'Cancelado', value: 'CANCELLED'},
    {label: 'Encerrado', value: 'CLOSED'},
  ]
  const visibilityOptions = [
    {label: 'Público', value: 'PUBLIC'},
    {label: 'Privado', value: 'PRIVATE'},
  ]
  const typeTicketOptions = [
    {label: 'Pago', value: 'PAID'},
    {label: 'Gratuito', value: 'FREE'},
  ]
  const visibilityTicketOptions = [
    {label: 'Aberto ao público', value: 'PUBLIC'},
    {label: 'Apenas convidados', value: 'GUESTS'},
    {label: 'Apenas produtor', value: 'PRIVATE'},
  ]

  const findEventBySlug = async (slug: string): Promise<Event> => {
    try {
      return await fetch<Event>(`${baseUrl}/pub/${slug}`)
    } catch (e: any) {
      return Promise.reject(getDefaultEvent())
    }
  }

  const saveAffiliates = async (urls: string[], id: string): Promise<void> => {
    await fetch<Event>(`${baseUrl}/affiliates/${id}`, {
        method: 'POST',
        body: JSON.stringify(urls)
      })
  }

  const findAllMyEvents = async (): Promise<Event[]> => {
    try {
      return await fetch<Event[]>(`${baseUrl}/my-events`)
    } catch (e: any) {
      return Promise.resolve([])
    }
  }

  const saveEvent = async (form: EventForm): Promise<Event | undefined> => {
    try {
      const cep = (form.cep || '').replaceAll(/[^0-9]/g, '')
      const {address, addressNumber, complement, neighborhood, city, state, addressId, place, ageGroup} = form
      const eventAddress = {cep, address, addressNumber, complement, neighborhood, city, state, place, id: addressId}
      delete form.cep
      delete form.address
      delete form.addressNumber
      delete form.complement
      delete form.neighborhood
      delete form.city
      delete form.state
      delete form.category
      delete form.addressId
      delete form.place
      const body = {...form, eventAddress}
      return await fetch<Event>(`${baseUrl}/store`, {
        method: 'POST',
        body
      })
    } catch (e: any) {
      Alert.error(e.message)
    }
  }

  const findAllEventAddresses = async (): Promise<EventAddress[]> => {
    try {
      return await fetch<EventAddress[]>(`${baseUrl}/addresses`)
    } catch (e: any) {
      return [];
    }
  }

  const findEventListForComboBox = async (): Promise<EventComboBox[]> => {
    try {
      return await fetch<EventComboBox[]>(`${baseUrl}/user-event-list`)
    } catch (e: any) {
      return [];
    }
  }

  const findAllEventsBySearch = async (search: EventSearch) => {
    try {
      return await fetch(`${baseUrl}/pub/search`, {
        method: 'POST',
        body: search
      })
    } catch (e: any) {
      return []
    }
  }

  const findAllDashboardData = async (eventId: string): Promise<DashboardData> => {
    try {
      const data = await fetch<string>(`${baseUrl}/${eventId}/dashboard`)
      const json = JSON.parse(data)
      return json.shift() || {} as DashboardData
    } catch (e: any) {
      return {} as DashboardData
    }
  }

  const eventToForm = (event: Event): EventForm => {
    if (!event) event = getDefaultEvent()
    return {
      id: event.id || '',
      ageGroup: event.ageGroup || 0,
      name: event.name || '',
      logo: event.logo || '',
      description: event.description || '',
      type: event.type || '',
      status: event.status || '',
      category: event.subCategory?.category?.id || undefined,
      subCategoryId: event.subCategory?.id || '',
      startAt: event.startAt || null,
      endAt: event.endAt || null,
      visibility: event.visibility || '',
      address: event.eventAddress?.address || '',
      place: event.eventAddress?.place || '',
      cep: event.eventAddress?.cep || '',
      addressId: event.eventAddress?.id || '',
      addressNumber: event.eventAddress?.addressNumber || '',
      complement: event.eventAddress?.complement || '',
      neighborhood: event.eventAddress?.neighborhood || '',
      city: event.eventAddress?.city || '',
      state: event.eventAddress?.state || '',
      metaPixel: event.metaPixel || '',
      googleAnalytics: event.googleAnalytics || '',
      tickets: (event.tickets || []).map((t: any) => {
        delete t.fee
        return t;
      }),
      useTerms: event.useTerms || false,
    };
  }

  const getDefaultEvent = (): Event => {
    return {
      id: undefined,
      type: 'IN_PERSON',
      status: 'DRAFT',
      name: '',
      slug: '',
      logo: '',
      description: '',
      subCategory: undefined,
      startAt: '',
      endAt: '',
      useTerms: true,
      visibility: 'PUBLIC',
      eventAddress: undefined,
      producerPageInfo: undefined,
      googleAnalytics: undefined,
      metaPixel: undefined,
      tickets: [],
      affiliates: []
    }
  }

  const getDefaultTicket = (): Ticket => {
    return {
      id: undefined,
      event: undefined,
      type: 'PAID',
      visibility: 'PUBLIC',
      title: '',
      obs: '',
      amount: 0,
      quantity: 0,
      quantityMinCheckout: 0,
      quantityMaxCheckout: 0,
      startAt: '',
      endAt: '',
      fee: 0,
    }
  }

  return {
    typeOptions, statusOptions, visibilityOptions, typeTicketOptions, visibilityTicketOptions,
    findEventBySlug, findAllMyEvents, saveEvent, findAllEventAddresses, findAllEventsBySearch, eventToForm,
    getDefaultEvent, getDefaultTicket, saveAffiliates ,findAllDashboardData, findEventListForComboBox
  }

}
